import React from 'react';
import { MapPin, Activity } from 'lucide-react';

const ComputeWorldMap = ({ clusters = [] }) => {
  // Process clusters to extract region-based compute locations
  const computeLocations = React.useMemo(() => {
    if (!clusters || clusters.length === 0) {
      return [];
    }

    // Group clusters by region
    const regionMap = {};
    clusters.forEach(cluster => {
      const region = cluster.region || 'Unknown';
      if (!regionMap[region]) {
        regionMap[region] = {
          name: region,
          region: cluster.cloud_provider ? cluster.cloud_provider.toUpperCase() : 'Cloud',
          active: 0,
          upCount: 0,
          stoppedCount: 0,
          initCount: 0
        };
      }

      regionMap[region].active++;
      if (cluster.status === 'UP') {
        regionMap[region].upCount++;
      } else if (cluster.status === 'STOPPED') {
        regionMap[region].stoppedCount++;
      } else if (cluster.status === 'INIT') {
        regionMap[region].initCount++;
      }
    });

    // Convert to array and determine overall status
    return Object.values(regionMap).map(location => ({
      ...location,
      status: location.upCount > 0 ? 'up' : location.stoppedCount > 0 ? 'stopped' : 'init'
    }));
  }, [clusters]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'up':
        return 'bg-green-500';
      case 'stopped':
        return 'bg-yellow-500';
      case 'init':
        return 'bg-blue-500';
      case 'offline':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getNodeSize = (active) => {
    if (active > 35) return 'w-4 h-4';
    if (active > 20) return 'w-3 h-3';
    return 'w-2 h-2';
  };

  return (
    <div className="w-full h-64 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <MapPin className="h-5 w-5 text-purple-600" />
          <h4 className="font-semibold text-gray-900">Global Compute Locations</h4>
        </div>
        <div className="flex items-center space-x-4 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Up</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span>Stopped</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Init</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 h-48 overflow-y-auto">
        {computeLocations.length === 0 ? (
          <div className="col-span-full flex items-center justify-center h-full text-gray-500 text-sm">
            No clusters deployed yet. Launch a cluster to see compute locations.
          </div>
        ) : (
          computeLocations.map((location, index) => (
            <div key={index} className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className={`${getNodeSize(location.active)} ${getStatusColor(location.status)} rounded-full`}></div>
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <Activity className="h-3 w-3" />
                  <span>{location.active}</span>
                </div>
              </div>
              <div className="text-sm font-medium text-gray-900 truncate">{location.name}</div>
              <div className="text-xs text-gray-500">{location.region}</div>
            </div>
          ))
        )}
      </div>

      {computeLocations.length > 0 && (
        <div className="mt-3 text-xs text-gray-500 text-center">
          Showing {computeLocations.length} region{computeLocations.length !== 1 ? 's' : ''} with {clusters.length} total cluster{clusters.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default ComputeWorldMap;